import React, { useState } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import { Button } from "@mui/material";
import "./NewEvent.css";
import { useAddEvent, useEvent } from "../../hooks/useEvent";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useEventType } from "../../hooks/useEventType";
import { StepOne } from "../Steps/StepOne/StepOne";
import { StepTwo } from "../Steps/StepTwo/StepTwo";
import { StepThree } from "../Steps/StepThree/StepThree";
import { StepFour } from "../Steps/StepFour/StepFour";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";

export default function NewEvent() {
  const navigate = useNavigate();
  const { mutate: addEvent } = useAddEvent();
  const { data: allEventsData } = useEvent();
  const { data: eventTypeData } = useEventType();

  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState({});

  const generateUniqueEventId = () => {
    const generateRandomId = () => {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      const num = array[0] % 1000000;
      const randomNum = num.toString().padStart(6, "0");
      return `AB${randomNum}`;
    };

    if (!allEventsData || allEventsData.length === 0) {
      return generateRandomId();
    }

    let newEventId = generateRandomId();
    let isUnique = false;

    while (!isUnique) {
      // Check if the generated ID exists in allEventsData
      const idExists = allEventsData.some(
        (event) => event.event_id === newEventId
      );

      if (!idExists) {
        isUnique = true;
      } else {
        // If ID exists, generate a new one
        newEventId = generateRandomId();
      }
    }

    return newEventId;
  };

  const [formData, setFormData] = useState({
    // Step One data
    event_id: generateUniqueEventId(),
    event_logo: "https://storage.googleapis.com/livesod/abbott/abbott_logo.png",
    created_by: "",
    created_by_name: "",
    name: "",
    city_id: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    event_type_id: "",
    allow_app_sign: false,
    organization_id: "",
    country_id: "",
    spend_type_id: "",
    promo_number: "",
    promo_approval_date: "",
    relationship_id: "",
    external_source_id: "",
    activity_type_id: "",
    attached_files: "",
    currency: "Saudi Riyal",
    division_id: "",
    isStepOneValid: false,
    // Step Two data
    terms:
      "I hereby certify that all the information provided by me is true and accurate and I accept and understand that my data may be transferred for the purposes described above, to other Abbott group companies based in the United States of America.",
    acceptAllTerms: false,
    // Step Three data
    bg_color: "#F7F7F7",
    font_color: "#000000",
    btn_color: "#159bd7",
  });

  const validationRules = {
    name: { required: true, minLength: 3 },
    city_id: { required: true },
    start_date: { required: true },
    end_date: { required: true },
    start_time: { required: true },
    end_time: { required: true },
    country_id: { required: true },
    attached_files: { required: true },
    currency: { required: true },
    division_id: { required: true },
    team_id: { required: true },
    event_type_id: { required: true },
    spend_type_id: { required: true },
    relationship_id: { required: true },
    external_source_id: { required: true },
    activity_type_id: { required: true },
    promo_approval_date: { required: true },
    promo_number: { required: true },
  };

  const validateForm = () => {
    const newErrors = {};
    const selectedEventType = eventTypeData?.find(
      (event) => event.id === formData.event_type_id
    );

    Object.entries(validationRules).forEach(([field, rules]) => {
      const value = formData[field];

      if (rules.required) {
        if (field === "attached_files") {
          if (!value || value.length === 0) {
            newErrors[field] = "Please attach required files";
          }
        } else if (field === "currency") {
          if (!value) {
            newErrors[field] = "Please select currency";
          }
        } else if (!value) {
          newErrors[field] = "This field is required";
        }
      }

      if (rules.minLength && value && value.length < rules.minLength) {
        newErrors[field] = `Must be at least ${rules.minLength} characters`;
      }
    });

    // Check organization_id requirement
    if (selectedEventType?.needs_organization && !formData.organization_id) {
      newErrors.organization_id =
        "Organization is required for this event type";
    }

    // Date validation logic
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);

      if (endDate < startDate) {
        newErrors.end_date = "End date cannot be before start date";
      }
    }

    // Time validation logic when dates are the same
    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_time &&
      formData.end_time
    ) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);

      // If dates are the same, check time
      if (startDate.getTime() === endDate.getTime()) {
        const startTime = formData.start_time;
        const endTime = formData.end_time;

        if (endTime < startTime) {
          newErrors.end_time =
            "End time cannot be before start time when dates are the same";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceedToNextStep = () => {
    switch (activeStep) {
      case 0:
        return formData.isStepOneValid;
      case 1:
        return formData.acceptAllTerms;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const isValid = validateForm();
      setFormData((prev) => ({
        ...prev,
        isStepOneValid: isValid,
      }));

      if (!isValid) {
        toast.error("Please fill all required fields");
        setOpen(true);
        return;
      }
    } else if (!canProceedToNextStep()) {
      toast.error("Please accept all terms and conditions");
      setOpen(true);
      return;
    }

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
    setOpen(false);
  };

  const updateFormData = (stepData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...stepData,
    }));
    setOpen(false);
  };

  const steps = [
    { stepTitle: "Event Details", stepComp: <StepOne /> },
    { stepTitle: "Modules", stepComp: <StepTwo /> },
    { stepTitle: "Theme", stepComp: <StepThree /> },
    { stepTitle: "Preview", stepComp: <StepFour /> },
  ];
  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      name: "",
      created_by: "",
      created_by_name: "",
      city_id: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      event_type_id: "",
      allow_app_sign: false,
      organization_id: "",
      country_id: "",
      spend_type_id: "",
      promo_number: "",
      promo_approval_date: "",
      relationship_id: "",
      external_source_id: "",
      activity_type_id: "",
      attached_files: "",
      currency: "Saudi Riyal",
      division_id: "",
      terms:
        "I hereby certify that all the information provided by me is true and accurate and I accept and understand that my data may be transferred for the purposes described above, to other Abbott group companies based in the United States of America.",
      acceptAllTerms: false,
      bg_color: "#F7F7F7",
      font_color: "#000000",
      btn_color: "#159bd7",
    });
  };

  const step = (num) => {
    switch (num) {
      case 0:
        return (
          <StepOne
            formData={formData}
            setFormData={setFormData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 1:
        return <StepTwo formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <StepThree formData={formData} updateFormData={updateFormData} />
        );
      case 3:
        return <StepFour formData={formData} />;
      default:
        break;
    }
  };

  const addNewEvent = async () => {
    try {
      const {
        created_by_name,
        event_id,
        isStepOneValid,
        organization_id,
        ...eventData
      } = formData;

      const formDataToSend = new FormData();
      formDataToSend.append("eventId", event_id);
      formDataToSend.append("file", formData.attached_files);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/attachments/upload`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        }
      );

      if (uploadResponse.data) {
        eventData.attached_files = `${process.env.REACT_APP_BACKEND_URL}/${uploadResponse.data.data.file.path}`;
      }

      let cityName = "";
      let countryName = "";

      if (eventData.city_id) {
        const citySnap = await getDoc(doc(db, "cities", eventData.city_id));
        if (citySnap.exists()) cityName = citySnap.data().name || "";
      }

      if (eventData.country_id) {
        const countrySnap = await getDoc(
          doc(db, "countries", eventData.country_id)
        );
        if (countrySnap.exists()) countryName = countrySnap.data().name || "";
      }

      // Format dates to yyyy/mm/dd
      const formattedEventData = {
        ...eventData,
        city_name: cityName,
        country_name: countryName,
        start_date: eventData.start_date
          ? new Date(eventData.start_date)
              .toISOString()
              .split("T")[0]
              .replace(/-/g, "/")
          : "",
        end_date: eventData.end_date
          ? new Date(eventData.end_date)
              .toISOString()
              .split("T")[0]
              .replace(/-/g, "/")
          : "",
        promo_approval_date: eventData.promo_approval_date
          ? new Date(eventData.promo_approval_date)
              .toISOString()
              .split("T")[0]
              .replace(/-/g, "/")
          : "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const eventPayload = {
        id: event_id,
        ...(organization_id && { organization_id }), // Add organization_id only if it exists
        ...formattedEventData,
      };

      addEvent(eventPayload);
      toast.success("Event added successfully!");

      setTimeout(() => {
        navigate("/events");
      }, 1000);
    } catch (error) {
      toast.error("Error adding event. Please try again.");
    }
  };

  return (
    <div>
      <ToastContainer />
      <Box sx={{ width: "100%" }}>
        <Stepper
          sx={{
            "& .MuiStepIcon-root.Mui-active": {
              color: "#159bd7",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: "#159bd7",
            },
          }}
          activeStep={activeStep}
          className="mb-4"
        >
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={`${label}-${index}`} {...stepProps}>
                <StepLabel {...labelProps}> {label.stepTitle}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {activeStep === steps.length ? (
          // LastStep --------------------------
          <>
            <Typography sx={{ mt: 2, mb: 1 }}>
              All steps completed - you&apos;re finished
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button onClick={handleReset}>Reset</Button>
            </Box>
          </>
        ) : (
          <>
            {/* StepContent  */}
            <div>{step(activeStep)}</div>
            <Box></Box>

            {/* End  */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 2,
                alignItems: "center",
              }}
            >
              <Button
                // color="inherit"
                // disabled={activeStep === 0}
                disabled={activeStep === 0 ? true : false}
                onClick={handleBack}
                sx={{
                  mr: 1,
                  backgroundColor: "#9ca3af",
                  width: "90px",
                  height: "40px",
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#9ca3af",
                  },
                }}
                className="backButton my-4 "
              >
                <i className="fa-solid fa-angles-left mr-2"></i>
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />

              {activeStep === steps.length - 1 ? (
                <Button
                  data-track="submit-add-event-button"
                  data-module="Events"
                  style={{
                    fontWeight: "bold",
                    backgroundColor: "#159bd7",
                    color: "white",
                  }}
                  className=" my-4"
                  onClick={addNewEvent}
                >
                  Submit
                </Button>
              ) : (
                <div>
                  {open && (
                    <small className="text-danger px-2 m-0">
                      Please Insert All Data
                    </small>
                  )}
                  <Button
                    style={{
                      width: "90px",
                      height: "40px",
                      fontWeight: "bold",
                      backgroundColor: "#159bd7",
                      color: "white",
                    }}
                    className="text-white my-4 mx-4 "
                    onClick={handleNext}
                  >
                    Next
                    <i className="fa-solid fa-angles-right ml-2"></i>
                  </Button>
                </div>
              )}
            </Box>
          </>
        )}
      </Box>
    </div>
  );
}
